import React from 'react';
import LegalPage from '../components/LegalPage';

const PRIVACY_HTML = `
  <div class="meta">
    <strong>Effective:</strong> May 29, 2026 &nbsp;·&nbsp;
    <strong>Statute:</strong> PIPEDA (Federal) + Ontario Privacy Law &nbsp;·&nbsp;
    <strong>Data Controller:</strong> GoogLyn AI Inc. &nbsp;·&nbsp;
    <strong>Version:</strong> 4.0
  </div>

  <div class="summary">
    <div class="label">Your Privacy in Plain Terms</div>
    <h2>What matters most</h2>
    <ul>
      <li>We collect only what we need to serve you.</li>
      <li>We never sell your personal information. Ever.</li>
      <li>We never train AI on your data.</li>
      <li>Payment data is handled by our PCI-compliant processor — we never see your full card number.</li>
      <li>We tell you clearly when data leaves Canada.</li>
      <li>You can access, correct, or delete your data anytime.</li>
      <li>We notify you within 72 hours of any breach that affects you.</li>
    </ul>
  </div>

  <h2>1. Who Controls Your Data</h2>
  <p><strong>GoogLyn AI Inc.</strong> is the data controller responsible for your personal information. Technical service providers who process data on our behalf are <strong>data processors</strong> — they act only on our instructions and cannot use your data for their own purposes.</p>

  <h2>2. What We Collect and Why</h2>
  <table class="tbl">
    <tr><th>What We Collect</th><th>How</th><th>Why</th></tr>
    <tr><td>Name, email, company, phone</td><td>You provide directly</td><td>Account creation and service delivery</td></tr>
    <tr><td>Billing address</td><td>You provide at checkout</td><td>Payment processing and tax calculation</td></tr>
    <tr><td>Payment method (tokenized)</td><td>Via payment processor</td><td>Processing your transactions securely</td></tr>
    <tr><td>Transaction history</td><td>Generated on purchase</td><td>Receipts, refunds, dispute resolution</td></tr>
    <tr><td>Service inputs and prompts</td><td>You submit when using Services</td><td>Generating your requested AI Outputs</td></tr>
    <tr><td>Communications</td><td>You contact us</td><td>Customer support and correspondence</td></tr>
    <tr><td>Usage and navigation data</td><td>Automatically via cookies</td><td>Site functionality and improvement</td></tr>
    <tr><td>Device and technical data</td><td>Automatically collected</td><td>Security and performance</td></tr>
    <tr><td>Analytics data</td><td>Via analytics providers (with consent)</td><td>Understanding how people use our site</td></tr>
  </table>
  <p><strong>Payment cards:</strong> We never store full card numbers. All payment data is tokenized and handled by our PCI-DSS compliant payment processor. We only store the last 4 digits, card type, and expiry date for your reference.</p>

  <h2>3. Consent Under PIPEDA</h2>
  <ul>
    <li><strong>Express consent</strong> — for marketing communications and non-essential cookies. You opt in actively.</li>
    <li><strong>Implied consent</strong> — for operational uses directly related to your use of the Services (e.g., processing your payment, generating your Outputs).</li>
    <li><strong>Withdrawal</strong> — You may withdraw consent at any time by contacting us. This may affect your ability to use certain features.</li>
  </ul>

  <h2>4. What We Don't Do</h2>
  <div class="box box-dark">
    <ul>
      <li><strong>We never sell your personal information</strong> to any third party.</li>
      <li><strong>We never use your content or inputs to train AI models</strong> — ours or anyone else's.</li>
      <li><strong>We never share your payment data</strong> with anyone other than our payment processor.</li>
      <li><strong>We never share your data</strong> for purposes unrelated to the Services without your explicit consent.</li>
    </ul>
  </div>

  <h2>5. Who We Share Your Data With</h2>
  <table class="tbl">
    <tr><th>Processor</th><th>Location</th><th>Purpose</th><th>Safeguard</th></tr>
    <tr><td>Anthropic, PBC</td><td>United States</td><td>AI model inference for Outputs</td><td>Data Processing Agreement</td></tr>
    <tr><td>Amazon Web Services</td><td>US / Canada</td><td>Cloud hosting and infrastructure</td><td>AWS Data Processing Addendum</td></tr>
    <tr><td>Payment processor</td><td>Canada / United States</td><td>Billing and transactions</td><td>PCI-DSS compliance + DPA</td></tr>
    <tr><td>Google Analytics</td><td>United States</td><td>Website analytics (consent-based)</td><td>Google Analytics DPA</td></tr>
  </table>
  <p>We may also disclose your information if required by law, court order, or governmental authority. In a merger or acquisition, your data may be transferred with advance notice and appropriate protections.</p>

  <h2>6. Payment Data Security</h2>
  <p>All payment transactions are encrypted using TLS technology. Our payment processor is PCI-DSS Level 1 compliant — the highest level of payment security certification. In the event of any security incident affecting payment data, we will notify you and the relevant authorities immediately in accordance with our breach notification obligations.</p>

  <h2>7. Transborder Data Flows</h2>
  <p>Some of our processors are located in the <strong>United States</strong>. Under PIPEDA, we ensure comparable protection through data processing agreements and contractual protections. You acknowledge that data transferred to the US may be subject to lawful access by US authorities under US law.</p>

  <h2>8. Data Retention Schedule</h2>
  <table class="tbl">
    <tr><th>Data Type</th><th>Retention Period</th><th>Reason</th></tr>
    <tr><td>Account information</td><td>Duration of account + 7 years</td><td>Ontario Statute of Limitations / Income Tax Act</td></tr>
    <tr><td>Payment and billing records</td><td>7 years</td><td>Canada Revenue Agency requirements</td></tr>
    <tr><td>Transaction history</td><td>7 years</td><td>Tax compliance and dispute resolution</td></tr>
    <tr><td>Service inputs and Outputs</td><td>90 days post-generation</td><td>Minimal collection principle</td></tr>
    <tr><td>Customer communications</td><td>3 years</td><td>Dispute resolution / legal compliance</td></tr>
    <tr><td>Analytics data</td><td>Up to 26 months</td><td>Google Analytics default</td></tr>
    <tr><td>Security logs</td><td>12 months</td><td>Security monitoring</td></tr>
  </table>

  <h2>9. Data Breach Notification</h2>
  <div class="box box-warn">
    <p>In the event of a breach creating a real risk of significant harm to you, we will notify you <strong>within 72 hours</strong>, report to the Office of the Privacy Commissioner of Canada, and maintain a breach record as required by PIPEDA.</p>
  </div>

  <h2>10. Your PIPEDA Rights</h2>
  <ul>
    <li><strong>Access</strong> — request a copy of personal information we hold about you;</li>
    <li><strong>Correction</strong> — challenge and correct inaccurate information;</li>
    <li><strong>Withdrawal of consent</strong> — withdraw consent to any processing based on consent;</li>
    <li><strong>Deletion</strong> — request deletion, subject to legal retention requirements;</li>
    <li><strong>Complaint</strong> — lodge a complaint with the Office of the Privacy Commissioner of Canada.</li>
  </ul>
  <p>Contact us at <a href="mailto:solutions@googlyn.ca">solutions@googlyn.ca</a>. We respond within <strong>30 days</strong>.</p>

  <h2>11. Security Safeguards</h2>
  <p>We implement TLS encryption in transit, access controls, role-based permissions, and secure AWS infrastructure. No system is 100% secure — we will promptly notify you of any breach affecting your data.</p>

  <h2>12. Children</h2>
  <p>Our Services are not directed to anyone under 18. We do not knowingly collect information from minors. Contact us immediately if you believe we have done so inadvertently.</p>

  <h2>13. Changes to This Policy</h2>
  <p>We will notify registered users of material changes by email at least 30 days before they take effect. The current version is always available at <a href="https://googlyn.ca">googlyn.ca</a>.</p>

  <div class="contact">
    <div class="label">Privacy Officer — GoogLyn AI Inc.</div>
    <p>Toronto, Canada</p>
    <p>Email: <a href="mailto:legal@googlyn.ca">legal@googlyn.ca</a></p>
    <p>To escalate: <a href="https://www.priv.gc.ca">Office of the Privacy Commissioner of Canada — priv.gc.ca</a> &nbsp;·&nbsp; 1-800-282-1376</p>
  </div>
`;

const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy" html={PRIVACY_HTML} />
);

export default PrivacyPolicy;
